import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setMarketplaceCartQuantity } from '../../redux/reducers/marketplaceCart';
import BackButton from '../../components/back-button/BackButton';
import Loader from '../../components/loader/Loader';
import SpecimenCard from '../../components/market-place/specimen-card/SpecimenCard';
import AnimalBanner from '../../components/animal/AnimalBanner';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, MARKETPLACE_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { MarketPlaceSpecimenResponse } from '../../types/interfaces/marketPlace.interfaces';
import { StandardParams, PagedResponse } from '../../types/interfaces/apiParams.interfaces';
import { Genders } from '../../types/enums';
import { addToMarketplaceCart, getMarketplaceCartItems } from '../../api/marketPlaceCartItemApi';
import { getMarketplaceSpecimenById, getMarketplaceSpecimens } from '../../api/marketPlaceSpecimenApi';
import { showToast } from '../../services/toast.service';
import { centsToDollars } from '../../services/account.service';
import { isWithinStockLimit } from '../../utils/commonUtils';
import { useGuestMarketplaceOptional } from '../../context/guestMarketplaceContext';
import './marketPlacePreview.scss';

const MORE_FROM_BREEDER_INCLUDE = 'Animal.Breed.Species, Animal.Attachments, MarketPlaceSpecimenTagMappings';

const buildMoreFromBreederParams = (accountId: number, currentId: number, skip: number, top: number): StandardParams => ({
  include: MORE_FROM_BREEDER_INCLUDE,
  filter: `AccountId eq ${accountId};IsDeleted eq false;MarketPlaceSpecimenId neq ${currentId}`,
  limit: top,
  offset: skip,
});

const isFemaleSpecimen = (specimen: MarketPlaceSpecimenResponse): boolean => {
  const gender = specimen.animal?.gender ?? specimen.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

type MarketPlacePreviewProps = {
  guestMode?: boolean;
};

const MarketPlacePreview: FC<MarketPlacePreviewProps> = ({ guestMode = false }) => {
  const { marketPlaceSpecimenId } = useParams();
  const navigate = useNavigate();
  const activeAccountId = useAppSelector(state => state.activeAccount.accountId);
  const dispatch = useAppDispatch();
  const guestMarketplace = useGuestMarketplaceOptional();
  const [specimen, setSpecimen] = useState<MarketPlaceSpecimenResponse | null>(null);
  const [moreFromBreeder, setMoreFromBreeder] = useState<MarketPlaceSpecimenResponse[]>([]);
  const [moreSkip, setMoreSkip] = useState<number>(0);
  const [hasMoreFromBreeder, setHasMoreFromBreeder] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [moreLoading, setMoreLoading] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<string>('1');
  const numericId = useMemo(() => (marketPlaceSpecimenId ? Number(marketPlaceSpecimenId) : Number.NaN), [marketPlaceSpecimenId]);

  const minimumQuantity = 1;
  const maxQuantity = Math.max(specimen?.quantity ?? minimumQuantity, minimumQuantity);
  const PageSize = 8;
  const isFemale = specimen ? isFemaleSpecimen(specimen) : false;
  const isOwnSpecimen =
    !guestMode && specimen?.accountId != null && activeAccountId != null && specimen.accountId === activeAccountId;

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isFemale) return;
    const raw = event.target.value;
    if (raw === '') {
      setQuantity('');
      return;
    }
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(Math.max(parsed, minimumQuantity), maxQuantity);
    setQuantity(String(clamped));
  };

  const handleQuantityBlur = () => {
    if (isFemale && specimen) {
      setQuantity(String(Math.max(specimen.quantity ?? minimumQuantity, minimumQuantity)));
      return;
    }
    if (quantity === '') setQuantity(String(minimumQuantity));
  };

  const fetchMoreFromBreeder = useCallback(
    async (accountId: number, offset: number, append: boolean) => {
      setMoreLoading(true);
      try {
        const res = await getMarketplaceSpecimens(buildMoreFromBreederParams(accountId, numericId, offset, PageSize));
        const payload = res.data as MarketPlaceSpecimenResponse[] | PagedResponse<MarketPlaceSpecimenResponse>;
        const items = Array.isArray(payload) ? payload : payload.result ?? [];
        const total = Array.isArray(payload) ? undefined : payload.total;

        setMoreFromBreeder(prev => (append ? [...prev, ...items] : items));
        setMoreSkip(offset + items.length);
        setHasMoreFromBreeder(total != null ? offset + items.length < total : items.length >= PageSize);
      } catch {
        showToast.error(toastMessages.FAILED_TO_FETCH_MARKETPLACE_SPECIMENS);
      } finally {
        setMoreLoading(false);
      }
    },
    [numericId],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (Number.isNaN(numericId)) {
        navigate(guestMode ? ROUTE_PATHS.PUBLIC_MARKETPLACE : ROUTE_PATHS.APP_MARKETPLACE);
        return;
      }

      try {
        setLoading(true);
        const include = 'Animal.Breed.Species, Animal.Attachments, Accounts, embryoStraw';
        const { data } = await getMarketplaceSpecimenById(numericId, { include });
        setSpecimen(data);
        if (isFemaleSpecimen(data)) {
          setQuantity(String(Math.max(data.quantity ?? minimumQuantity, minimumQuantity)));
        } else {
          setQuantity(String(minimumQuantity));
        }

        if (data?.accountId) {
          setMoreFromBreeder([]);
          setMoreSkip(0);
          setHasMoreFromBreeder(true);
          await fetchMoreFromBreeder(data.accountId, 0, false);
        }
      } catch {
        showToast.error(toastMessages.FAILED_TO_FETCH_MARKETPLACE_SPECIMENS);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [numericId, navigate, fetchMoreFromBreeder, guestMode]);

  const handleCardPreview = (id: number) => {
    if (guestMode) {
      navigate(`${ROUTE_PATHS.PUBLIC_MARKETPLACE_}${id}`);
    } else {
      navigate(ROUTE_PATHS.APP_MARKETPLACE_VIEW + id);
    }
  };

  const refreshMarketplaceCartQuantity = async () => {
    try {
      const { data: cartItems } = await getMarketplaceCartItems({
        include: 'Accounts, MarketPlaceSpecimen.Specimen.Animal, MarketPlaceSpecimen.Specimen.SpecimenType',
        filter: `accountId eq ${activeAccountId}`,
      });
      const totalQty = (cartItems ?? []).reduce((sum, item) => sum + (item.cartQuantity ?? 0), 0);
      dispatch(setMarketplaceCartQuantity(totalQty));
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleAddToCart = async (specimenToAdd: MarketPlaceSpecimenResponse, quantityToAdd: number) => {
    if (!guestMode && activeAccountId != null && specimenToAdd.accountId === activeAccountId) {
      return;
    }
    const isFemaleAnimal = isFemaleSpecimen(specimenToAdd);
    const finalQuantity = isFemaleAnimal
      ? Math.max(specimenToAdd.quantity ?? minimumQuantity, minimumQuantity)
      : Math.max(quantityToAdd || minimumQuantity, minimumQuantity);
    if (guestMode && guestMarketplace) {
      const availableStock = specimenToAdd.quantity ?? 0;
      if (!isWithinStockLimit(specimenToAdd.marketPlaceSpecimenId, finalQuantity, availableStock)) {
        showToast.error(toastMessages.MARKETPLACE_CART_ITEM_MAXED_OUT);
        return;
      }
      guestMarketplace.addGuestSpecimenToCart(specimenToAdd, finalQuantity);
      showToast.success(toastMessages.ADD_TO_MARKETPLACE_CART_SUCCESS);
      return;
    }
    try {
      await addToMarketplaceCart({
        accountId: activeAccountId ?? 0,
        quantity: finalQuantity,
        sellerAccountId: specimenToAdd.accountId,
        specimenId: specimenToAdd.specimenId,
        marketPlaceSpecimenId: specimenToAdd.marketPlaceSpecimenId,
        ...(isFemaleAnimal ? { embryoStrawId: specimenToAdd.embryoStrawId ?? 0 } : {}),
      });
      showToast.success(toastMessages.ADD_TO_MARKETPLACE_CART_SUCCESS);
      await refreshMarketplaceCartQuantity();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  if (loading) {
    return <Loader loaderSize="medium" pageLoader />;
  }

  if (!specimen) {
    return (
      <div className="marketplace marketplace-preview marketplace--preview">
        <BackButton onClick={() => navigate(guestMode ? ROUTE_PATHS.PUBLIC_MARKETPLACE : ROUTE_PATHS.APP_MARKETPLACE)} />
        <div className="marketplace-preview__empty">{MARKETPLACE_CONSTANTS.NO_MARKETPLACE_SPECIMEN}</div>
      </div>
    );
  }

  const priceInDollars = centsToDollars(specimen.unitPriceInCents);
  const displayPrice = typeof priceInDollars === 'number' ? priceInDollars.toFixed(2) : priceInDollars;
  const profilePath = guestMode
    ? `${ROUTE_PATHS.PUBLIC_MARKETPLACE_PROFILE_VIEW}${specimen.accountId}`
    : `${ROUTE_PATHS.APP_MARKETPLACE_VIEW}profile/${specimen.accountId}`;

  return (
    <div className="marketplace marketplace-preview marketplace--preview">
      <BackButton onClick={() => navigate(guestMode ? ROUTE_PATHS.PUBLIC_MARKETPLACE : ROUTE_PATHS.APP_MARKETPLACE)} />

      <section className="marketplace-preview__main">
        <div className="marketplace-preview__banner">
          <AnimalBanner
            animalId={specimen.animalId ? String(specimen.animalId) : undefined}
            animal={specimen.animal as any}
            embryoStraw={
              isFemale && specimen.embryoStraw
                ? {
                    embryoStrawCode: specimen.embryoStraw.embryoStrawCode,
                    embryoStage: specimen.embryoStraw.embryoStage,
                    embryoGrade: specimen.embryoStraw.embryoGrade,
                  }
                : undefined
            }>
            <div className="marketplace-preview__quantity-info">
              <div className="quantity">
                <h3>{LABEL_CONSTANTS.BREEDER}</h3>
                {specimen.accountId ? (
                  <span>
                    <Link className="marketplace-preview__breeder-link" to={profilePath}>
                      {specimen?.account?.name ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}
                    </Link>
                  </span>
                ) : (
                  <span>{specimen?.account?.name ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE}</span>
                )}
              </div>
              <div className="quantity">
                <h3>{LABEL_CONSTANTS.STOCK}</h3>
                <span>
                  {specimen.quantity ?? MARKETPLACE_CONSTANTS.EMPTY_VALUE} {LABEL_CONSTANTS.UNITS}
                </span>
              </div>
            </div>
            <div className="marketplace-preview__purchase-row">
              <div className="marketplace-preview__price">
                <span className="amount">${displayPrice}</span>
                <span className="per">{MARKETPLACE_CONSTANTS.PER_UNIT}</span>
              </div>
              <div className="marketplace-preview__actions">
                <span className="marketplace-preview__quantity-label">{LABEL_CONSTANTS.QUANTITY}</span>
                <input
                  type="number"
                  min={minimumQuantity}
                  max={maxQuantity}
                  className="marketplace-preview__quantity-input"
                  placeholder="1"
                  aria-label="Quantity"
                  value={quantity}
                  disabled={isFemale}
                  onChange={handleQuantityChange}
                  onBlur={handleQuantityBlur}
                />
                <button
                  className="button blue medium"
                  type="button"
                  disabled={isOwnSpecimen}
                  onClick={() => void handleAddToCart(specimen, Number(quantity))}>
                  {BUTTON_CONSTANTS.ADD_TO_CART}
                </button>
              </div>
            </div>
          </AnimalBanner>
        </div>
      </section>

      {Boolean(specimen?.accountId && (moreFromBreeder.length > 0 || moreLoading)) && (
        <section className="marketplace-preview__more">
          <h2 className="marketplace-preview__more-title">{MARKETPLACE_CONSTANTS.MORE_FROM_BREEDER}</h2>
          <div className="marketplace-preview__more-grid">
            {moreFromBreeder.map(item => (
              <SpecimenCard
                key={item.marketPlaceSpecimenId}
                animalId={item.animal?.animalId}
                name={item.animal?.name}
                attachments={item.animal?.attachments}
                breed={item.animal?.breed?.name}
                species={item.animal?.breed?.species?.name}
                gender={item.animal?.gender}
                quantity={item.quantity}
                price={item.unitPriceInCents}
                accountId={item.accountId}
                breederName={specimen?.account?.name}
                marketplaceSpecimenTagIds={item.marketPlaceSpecimenTagMappings?.map(
                  marketPlaceSpecimenTagMapping => marketPlaceSpecimenTagMapping.marketPlaceSpecimenTagId,
                )}
                onPreview={() => handleCardPreview(item.marketPlaceSpecimenId)}
                disableAddToCart={!guestMode && activeAccountId != null && activeAccountId === item.accountId}
                onAddToCart={() => {
                  const isFemaleAnimal = isFemaleSpecimen(item);
                  const qty = isFemaleAnimal ? Math.max(item.quantity ?? minimumQuantity, minimumQuantity) : 1;
                  void handleAddToCart(item, qty);
                }}
              />
            ))}
          </div>
          {moreLoading && <Loader loaderSize="medium" />}
          {Boolean(hasMoreFromBreeder && !moreLoading && specimen?.accountId) && (
            <div className="marketplace-preview__more-actions">
              <button
                type="button"
                className="button blue"
                onClick={() => {
                  if (specimen?.accountId != null) fetchMoreFromBreeder(specimen.accountId, moreSkip, true);
                }}>
                {BUTTON_CONSTANTS.VIEW_MORE}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default MarketPlacePreview;
